import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DocumentUpload from '../components/DocumentUpload';
import documentsService, { Document } from '../services/documentsService';
import {
  DocumentTextIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  XMarkIcon,
  EyeIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

export default function Documents() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const docs = await documentsService.getDocuments();
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilesUploaded = (files: any[]) => {
    setShowUploadModal(false);
    fetchDocuments();
  };

  const downloadDocument = async (doc: Document) => {
    try {
      const downloadUrl = documentsService.getDocumentUrl(doc.file_path);
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_name || doc.name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const deleteDocument = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const success = await documentsService.deleteDocument(docId);
      if (success) {
        setDocuments(prev => prev.filter(d => d.id !== docId));
        if (selectedDocument?.id === docId) {
          setSelectedDocument(null);
        }
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDocumentThumbnail = (doc: Document) => {
    // If we have a thumbnail path, use it
    if (doc.thumbnail_path) {
      return documentsService.getThumbnailUrl(doc.thumbnail_path);
    }

    // For PDFs, we could generate a thumbnail on the server
    // For now, return a placeholder based on file type
    if (doc.mime_type?.includes('pdf')) {
      return null; // Will use PDF icon
    } else if (doc.mime_type?.startsWith('image/')) {
      return documentsService.getDocumentUrl(doc.file_path); // Use the image itself as thumbnail
    }
    return null;
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject = selectedProject === 'all' || doc.project_id === selectedProject;
    return matchesSearch && matchesProject;
  });

  const projects = Array.from(new Set(documents.map(d => d.project_id).filter(Boolean)));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Document Library</h1>
          <p className="text-gray-600">Manage all your construction documents in one place</p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Project Filter */}
            {projects.length > 0 && (
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Projects</option>
                {projects.map(project => (
                  <option key={project} value={project}>
                    {project}
                  </option>
                ))}
              </select>
            )}

            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Upload Documents
            </button>
          </div>
        </div>

        {/* Documents Grid - Thumbnail View */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading documents...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-16 text-center">
            <FolderIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No documents yet</h3>
            <p className="text-gray-600 mb-6">Upload your first document to get started</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Upload Documents
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredDocuments.map((doc) => {
              const thumbnail = getDocumentThumbnail(doc);
              const isPDF = doc.mime_type?.includes('pdf');
              const isImage = doc.mime_type?.startsWith('image/');
              
              return (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedDocument(doc)}
                >
                  <div className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden">
                    {/* Thumbnail */}
                    <div className="aspect-square bg-gray-100 relative overflow-hidden">
                      {thumbnail && isImage ? (
                        <img
                          src={thumbnail}
                          alt={doc.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                          <DocumentTextIcon className={`${isPDF ? 'text-red-500' : 'text-blue-500'} h-16 w-16`} />
                        </div>
                      )}
                      
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                        <EyeIcon className="text-white h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </div>
                    </div>

                    {/* Document Info */}
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-gray-900 truncate mb-1" title={doc.name}>
                        {doc.name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(doc.file_size)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDate(doc.created_at)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Document Preview Modal */}
        <AnimatePresence>
          {selectedDocument && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedDocument(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedDocument.name}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatFileSize(selectedDocument.size)} • {formatDate(selectedDocument.uploadedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => downloadDocument(selectedDocument)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                      Download
                    </button>
                    <button
                      onClick={() => {
                        deleteDocument(selectedDocument.id);
                        setSelectedDocument(null);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <TrashIcon className="h-5 w-5" />
                      Delete
                    </button>
                    <button
                      onClick={() => setSelectedDocument(null)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6 text-gray-500" />
                    </button>
                  </div>
                </div>

                {/* Document Preview */}
                <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
                  {selectedDocument.mime_type?.includes('pdf') ? (
                    <iframe
                      src={documentsService.getDocumentUrl(selectedDocument.file_path)}
                      className="w-full h-[70vh] border rounded-lg"
                      title={selectedDocument.name}
                    />
                  ) : selectedDocument.mime_type?.startsWith('image/') ? (
                    <img
                      src={documentsService.getDocumentUrl(selectedDocument.file_path)}
                      alt={selectedDocument.name}
                      className="max-w-full h-auto mx-auto rounded-lg"
                    />
                  ) : (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-24 w-24 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Preview not available for this file type</p>
                      <button
                        onClick={() => downloadDocument(selectedDocument)}
                        className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Download to View
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upload Modal */}
        <AnimatePresence>
          {showUploadModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowUploadModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-xl max-w-2xl w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold text-gray-900">Upload Documents</h2>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6 text-gray-500" />
                  </button>
                </div>
                
                <DocumentUpload 
                  onFilesUploaded={handleFilesUploaded}
                  maxFiles={10}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}